<template>
  <div class="card card-custom card-shadowless border mb-5">
    <div class="card-header border-0 py-4">
      <div class="card-title align-items-center">
        <span class="card-label font-weight-bolder text-dark">
          <i class="fa fa-file-invoice-dollar mr-2 text-primary"></i>
          Manual IBAIS processing
        </span>
      </div>
      <div class="card-toolbar">
        <b-badge :variant="statusBadgeVariant" class="font-weight-bold p-2">
          {{ statusBadgeLabel }}
        </b-badge>
      </div>
    </div>

    <div class="card-body pt-2 pb-5">
      <p class="text-muted font-size-sm mb-3">
        Interim gate for Rentsure and CRI. Record the billing instruction for the
        IBAIS team, mark awaiting processing, then enter the IBAIS invoice number
        when posted. This replaces the old “finalised in IBAIS” habit on Bind Cover.
        Fuse MSI keeps Stamp → Schedule → INSIGHT Post — this rail stays closed for Fuse.
      </p>

      <div v-if="loading" class="text-muted font-size-sm">
        <i class="fa fa-spinner fa-spin mr-1"></i> Loading Manual IBAIS status…
      </div>

      <template v-else>
        <div class="form-group mb-4">
          <label class="font-weight-bold">Billing instruction</label>
          <textarea
            v-model="instructionDraft"
            class="form-control"
            rows="3"
            :disabled="isPosted || busy"
            placeholder="What the IBAIS processing team should do…"
          ></textarea>
        </div>

        <div class="form-group mb-4" v-if="!isPosted">
          <label class="font-weight-bold">IBAIS invoice number</label>
          <input
            v-model="invoiceDraft"
            type="text"
            class="form-control"
            :disabled="busy"
            placeholder="e.g. R-… or C-…"
          />
          <span class="form-text text-muted">
            Required only when marking Posted. Stored on K_InvoiceNo — never Insight invoice fields.
          </span>
        </div>
        <div v-else class="font-size-sm text-success mb-4">
          <i class="fa fa-check-circle"></i>
          Posted · invoice {{ status.invoiceNumber || "—" }}
        </div>

        <div class="d-flex flex-wrap align-items-center">
          <button
            type="button"
            class="btn btn-light-primary font-weight-bold mr-3 mb-2"
            :disabled="isPosted || busy || !instructionDraft"
            @click.prevent="saveInstruction"
          >
            Save instruction
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold mr-3 mb-2"
            :disabled="isPosted || busy || !instructionDraft"
            @click.prevent="markAwaiting"
          >
            Mark awaiting processing
          </button>
          <button
            type="button"
            class="btn btn-success font-weight-bold mr-3 mb-2"
            :disabled="isPosted || busy || !invoiceDraft"
            @click.prevent="markPosted"
          >
            Mark posted
          </button>
          <button
            type="button"
            class="btn btn-sm btn-light font-weight-bold mb-2"
            :disabled="busy"
            @click.prevent="loadStatus"
          >
            <i class="fa fa-sync" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
        </div>

        <b-alert v-if="error" variant="warning" show class="mt-4 mb-0">
          <div class="text-dark">{{ error }}</div>
        </b-alert>
        <b-alert v-else-if="info" variant="success" show class="mt-4 mb-0">
          <div class="text-dark">{{ info }}</div>
        </b-alert>
      </template>
    </div>
  </div>
</template>

<script>
import {
  getManualIbaisStatus,
  saveManualIbaisInstruction,
  markManualIbaisAwaiting,
  completeManualIbais
} from "@/services/workbench-service";

export default {
  name: "ManualIbaisProcessingRail",

  props: {
    ledgerRowId: {
      type: String,
      required: true
    },
    brandKey: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      loading: false,
      busy: false,
      status: null,
      error: "",
      info: "",
      instructionDraft: "",
      invoiceDraft: ""
    };
  },

  computed: {
    isPosted() {
      return !!(this.status && this.status.status === "Posted");
    },
    statusBadgeLabel() {
      if (!this.status) return "—";
      return this.status.statusLabel || this.status.status || "—";
    },
    statusBadgeVariant() {
      if (this.isPosted) return "success";
      if (this.status && this.status.status === "Awaiting_Processing") return "warning";
      return "secondary";
    }
  },

  watch: {
    ledgerRowId: {
      immediate: true,
      handler() {
        this.loadStatus();
      }
    }
  },

  methods: {
    softError(result, fallback) {
      if (result && result.error) return String(result.error);
      return fallback || "Unable to update Manual IBAIS status.";
    },

    async loadStatus() {
      if (!this.ledgerRowId) return;
      this.loading = true;
      this.error = "";
      try {
        const result = await getManualIbaisStatus(this.ledgerRowId);
        if (!result || result.ok === false) {
          this.status = null;
          this.error = this.softError(result, "Unable to load Manual IBAIS status.");
          return;
        }
        this.status = result;
        this.instructionDraft = result.billingInstruction || "";
        this.invoiceDraft = result.invoiceNumber || "";
      } catch (e) {
        this.error = (e && e.message) || "Unable to load Manual IBAIS status.";
      } finally {
        this.loading = false;
      }
    },

    async saveInstruction() {
      if (
        !window.confirm(
          "Save this billing instruction for the IBAIS processing team?"
        )
      ) {
        return;
      }
      this.busy = true;
      this.error = "";
      this.info = "";
      try {
        const result = await saveManualIbaisInstruction(this.ledgerRowId, {
          confirmed: true,
          billingInstruction: this.instructionDraft
        });
        if (!result || result.ok === false) {
          this.error = this.softError(result);
          return;
        }
        this.status = result;
        this.info = "Billing instruction saved.";
      } catch (e) {
        this.error = (e && e.message) || "Unable to save instruction.";
      } finally {
        this.busy = false;
      }
    },

    async markAwaiting() {
      if (
        !window.confirm(
          "Mark this transaction as awaiting IBAIS processing? The billing instruction will be stored for the processing team."
        )
      ) {
        return;
      }
      this.busy = true;
      this.error = "";
      this.info = "";
      try {
        const result = await markManualIbaisAwaiting(this.ledgerRowId, {
          confirmed: true,
          billingInstruction: this.instructionDraft
        });
        if (!result || result.ok === false) {
          this.error = this.softError(result);
          return;
        }
        this.status = result;
        this.info = "Marked awaiting processing.";
      } catch (e) {
        this.error = (e && e.message) || "Unable to mark awaiting.";
      } finally {
        this.busy = false;
      }
    },

    async markPosted() {
      if (
        !window.confirm(
          `Mark Posted with IBAIS invoice ${String(this.invoiceDraft || "").trim()}? This does not Post to INSIGHT.`
        )
      ) {
        return;
      }
      this.busy = true;
      this.error = "";
      this.info = "";
      try {
        const result = await completeManualIbais(this.ledgerRowId, {
          confirmed: true,
          invoiceNumber: this.invoiceDraft
        });
        if (!result || result.ok === false) {
          this.error = this.softError(result);
          return;
        }
        this.status = result;
        this.invoiceDraft = result.invoiceNumber || this.invoiceDraft;
        this.info = "Marked Posted in Manual IBAIS.";
        this.$emit("posted", result);
      } catch (e) {
        this.error = (e && e.message) || "Unable to mark posted.";
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
